import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLocations } from '../context/LocationContext';
import TopBar from '../components/TopBar';
import { MapPin, Star, Plus, ExternalLink, Settings2 } from 'lucide-react';

const Locations = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { selectedLocations, fetchLocations } = useLocations();
  const [selectedLocationId, setSelectedLocationId] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchLocations().catch(console.error);
  }, [isAuthenticated, navigate, fetchLocations]);

  // Mock locations data
  const mockLocations = selectedLocations.length > 0 ? selectedLocations : [
    {
      id: 1,
      name: 'Downtown Cafe & Bistro',
      address: '123 Main Street, New York, NY 10001',
      rating: 4.8,
      reviewCount: 1234,
      status: 'active'
    },
    {
      id: 2,
      name: 'Riverside Restaurant',
      address: '456 River Road, Brooklyn, NY 11201',
      rating: 4.5,
      reviewCount: 856,
      status: 'active'
    },
    {
      id: 3,
      name: 'Sunset Grill',
      address: '789 Beach Ave, Queens, NY 11358',
      rating: 4.7,
      reviewCount: 642,
      status: 'active'
    }
  ];

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex flex-col w-full min-h-screen bg-slate-50">
      <TopBar />

      <div className="flex-1 p-8">
        <div className="max-w-6xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Locations</h1>
            <p className="text-slate-600 mt-1">Manage your Google Business profiles</p>
          </div>
          <button 
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
            data-testid="add-location-btn"
          >
            <Plus className="w-5 h-5" />
            <span>Add Location</span>
          </button>
        </div>

        {/* Location Switcher Dropdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <label className="block text-sm font-semibold text-slate-700 mb-3">
            Active Location
          </label>
          <select 
            className="w-full px-4 py-3 bg-white border-2 border-slate-200 rounded-xl text-slate-900 font-medium focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={selectedLocationId || ''}
            onChange={(e) => setSelectedLocationId(e.target.value)}
            data-testid="location-switcher"
          >
            <option value="">Select a location</option>
            {mockLocations.map(loc => (
              <option key={loc.id} value={loc.id}>
                {loc.name} - {loc.address}
              </option>
            ))}
          </select>
        </div>

        {/* Locations Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mockLocations.map((location) => (
            <div
              key={location.id}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-lg transition-all duration-300"
              data-testid="location-card"
            >
              {/* Location Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900">{location.name}</h3>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700 mt-1">
                      {location.status}
                    </span>
                  </div>
                </div>
              </div>

              {/* Address */}
              <div className="mb-4">
                <p className="text-sm text-slate-600 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {location.address}
                </p>
              </div>

              {/* Rating and Reviews */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-lg">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-bold text-slate-900">{location.rating}</span>
                </div>
                <span className="text-sm text-slate-600">
                  {location.reviewCount} reviews
                </span>
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                  <ExternalLink className="w-4 h-4" />
                  <span>Switch</span>
                </button>
                <button className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold py-2.5 px-4 rounded-lg transition-colors flex items-center justify-center gap-2">
                  <Settings2 className="w-4 h-4" />
                  <span>Manage</span>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State if no locations */}
        {mockLocations.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-slate-300">
            <div className="text-6xl mb-4">📍</div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Locations Yet</h3>
            <p className="text-slate-600 mb-6">Add your first Google Business location to get started</p>
            <button className="bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold px-8 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all inline-flex items-center gap-2">
              <Plus className="w-5 h-5" />
              <span>Add Your First Location</span>
            </button>
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default Locations;
